import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router';
import { ApiError, ToastUtil } from '@axiom/ui';

import type { ReadCandidatesResponseType } from '../api/candidate-api';
import { CandidateApi } from '../api/candidate-api';

import type { SidebarPanel } from './AiSearchContextProvider';
import { AiSearchContextProvider } from './AiSearchContextProvider';

export const extractTalentCandidateId = ({
  pathname,
}: {
  pathname: string;
}): string | null => {
  const match = pathname.match(/^\/talent\/([^/]+)/);
  return match?.[1] ?? null;
};

export const getToastTalentLink = ({
  storedCandidateId,
  currentPathname,
}: {
  storedCandidateId: string | null;
  currentPathname: string;
}): string => {
  if (currentPathname.startsWith('/talent')) {
    const currentCandidateId = extractTalentCandidateId({
      pathname: currentPathname,
    });
    const candidateId = currentCandidateId || storedCandidateId;
    return candidateId ? `/talent/${candidateId}` : '/talent';
  }
  return storedCandidateId ? `/talent/${storedCandidateId}` : '/talent';
};

const EMPTY_RESULTS: ReadCandidatesResponseType = {
  data: [],
  meta: { resultCount: 0, currentPage: 1, pageCount: 1 },
};

export const AiSearchProvider = ({ children }: React.PropsWithChildren) => {
  const [isActive, setIsActive] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [results, setResults] = useState<ReadCandidatesResponseType | null>(
    null
  );
  const [error, setError] = useState<string | null>(null);
  const [activePanel, setActivePanel] = useState<SidebarPanel>('search');
  const requestIdRef = useRef(0);
  const storedCandidateIdRef = useRef<string | null>(null);
  // Per-search AbortController. cancelSearch() and a new search() both abort
  // the previous one so the in-flight fetch is actually cancelled (not just
  // ignored). requestIdRef remains as a safety net for late subject.next.
  const abortControllerRef = useRef<AbortController | null>(null);

  const search = useCallback(async (text: string) => {
    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;
    const requestId = ++requestIdRef.current;
    storedCandidateIdRef.current = extractTalentCandidateId({
      pathname: window.location.pathname,
    });
    setIsActive(true);
    setIsLoading(true);
    setSearchText(text);
    setError(null);
    // Reset results so CandidatesList's grid drops back into its
    // "Fetching data..." state on every new search, not just the first one.
    setResults(null);
    CandidateApi.clearAiSearchCandidates();

    const addCompletionToast = (
      message: string,
      linkLabel: string,
      type?: 'error'
    ) => {
      const talentLink = getToastTalentLink({
        storedCandidateId: storedCandidateIdRef.current,
        currentPathname: window.location.pathname,
      });
      const addedOn = ToastUtil.add({
        type,
        dismissible: true,
        children: (
          <>
            {message}
            <br />
            <Link
              to={talentLink}
              onClick={() => {
                const toast = ToastUtil.getAll().find(
                  t => t.addedOn === addedOn
                );
                if (toast) ToastUtil.remove(toast);
              }}
            >
              {linkLabel}
            </Link>
          </>
        ),
      });
    };

    const handleFailure = (status?: number) => {
      if (requestId !== requestIdRef.current) return;
      setError('The search failed. Please try again.');
      setIsLoading(false);
      // Materialize an empty result so the grid exits ag-grid's loading state
      // instead of staying in its infinite-fetch loop.
      setResults(EMPTY_RESULTS);
      const suffix = status ? ` (HTTP ${status})` : '';
      addCompletionToast(
        `Your AI Talent Search did not complete${suffix}.`,
        'Try again',
        'error'
      );
    };

    try {
      // EndpointCache.toPromise() is typed never-null but resolves with null
      // on failure; widen via unknown so the data/meta null checks below are
      // honest. The server has also been observed returning {data:null,
      // meta:null} as a "success" body, so the inner fields are widened too.
      // Hold the watcher reference so we can pull the HTTP status off its
      // ApiError when the promise resolves to null.
      const watcher = CandidateApi.readAiSearchCandidates({
        searchRequest: text,
        signal: controller.signal,
      });
      // Race the response against the abort signal. EndpointCache's catch
      // handler only fires subject.next(null) when its nextRequestProps still
      // matches; an in-flight abort doesn't always satisfy that, so without
      // this race the await could hang indefinitely on cancellation.
      const responsePromise = watcher.toPromise() as unknown as Promise<{
        data: ReadCandidatesResponseType['data'] | null;
        meta: ReadCandidatesResponseType['meta'] | null;
      } | null>;
      const abortPromise = new Promise<null>(resolve => {
        if (controller.signal.aborted) {
          resolve(null);
        } else {
          controller.signal.addEventListener(
            'abort',
            () => {
              resolve(null);
            },
            {
              once: true,
            }
          );
        }
      });
      const response = await Promise.race([responsePromise, abortPromise]);
      if (requestId !== requestIdRef.current) return;

      const data = response?.data;
      const meta = response?.meta;
      if (!data || !meta) {
        const err = watcher.getError();
        handleFailure(
          err instanceof ApiError ? err.getStatusCode() : undefined
        );
        return;
      }

      setResults({ data, meta });
      setIsLoading(false);
      addCompletionToast('AI Talent Search results are ready!', 'View results');
    } catch (e) {
      handleFailure(e instanceof ApiError ? e.getStatusCode() : undefined);
    }
  }, []);

  const cancelSearch = useCallback(() => {
    abortControllerRef.current?.abort();
    abortControllerRef.current = null;
    requestIdRef.current++;
    setIsActive(false);
    setIsLoading(false);
    setSearchText('');
    setResults(null);
    setError(null);
  }, []);

  const value = useMemo(
    () => ({
      isActive,
      isLoading,
      searchText,
      results,
      error,
      activePanel,
      setActivePanel,
      search,
      cancelSearch,
    }),
    [
      isActive,
      isLoading,
      searchText,
      results,
      error,
      activePanel,
      search,
      cancelSearch,
    ]
  );

  return (
    <AiSearchContextProvider.Provider value={value}>
      {children}
    </AiSearchContextProvider.Provider>
  );
};
